
import { Card, CardContent } from "@/components/ui/card";
import { useEnhancedColorThemes } from "@/hooks/useEnhancedColorThemes";

interface Character {
  id: string;
  name: string;
  species: string;
  role: string;
  description: string;
  personality: string;
  teaching_focus: string;
  special_talents: string;
  fun_facts: any;
  image_url: string;
  avatar_url: string;
  color_theme: string;
  age_group: string;
  featured: boolean;
}

interface CharacterInfoProps {
  character: Character;
}

export const CharacterInfo = ({ character }: CharacterInfoProps) => {
  const { getEnhancedTheme } = useEnhancedColorThemes();
  const funFactsList = character.fun_facts && Array.isArray(character.fun_facts) 
    ? character.fun_facts 
    : [];

  const enhancedTheme = getEnhancedTheme(character.color_theme);

  return (
    <div className="space-y-8" role="main" aria-label="Character information">
      {/* Character Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Information Card */}
        <Card className="border-l-4" style={{ borderLeftColor: enhancedTheme.baseColor }}>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: enhancedTheme.baseColor }}>
              <div 
                className="w-3 h-3 rounded-full mr-3" 
                style={{ backgroundColor: enhancedTheme.baseColor }}
                aria-hidden="true"
              ></div>
              Character Info
            </h2>
            <dl className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <dt className="font-medium text-gray-600">Name:</dt>
                <dd className="font-semibold text-gray-900">{character.name}</dd>
              </div>
              {character.species && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <dt className="font-medium text-gray-600">Species:</dt>
                  <dd className="font-semibold text-gray-900">{character.species}</dd>
                </div>
              )}
              {character.role && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <dt className="font-medium text-gray-600">Role:</dt>
                  <dd className="font-semibold text-gray-900">{character.role}</dd>
                </div>
              )}
              {character.age_group && (
                <div className="flex justify-between items-center py-2">
                  <dt className="font-medium text-gray-600">Age Group:</dt>
                  <dd className="font-semibold text-gray-900">{character.age_group}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Personality */}
        {character.personality && (
          <Card className="border-l-4 border-green-500">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3" aria-hidden="true"></div>
                Personality
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {character.personality}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Teaching Focus */}
        {character.teaching_focus && (
          <Card className="border-l-4 border-blue-500">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-blue-700 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3" aria-hidden="true"></div>
                Teaching Focus
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {character.teaching_focus}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Special Talents */}
        {character.special_talents && (
          <Card className="border-l-4 border-orange-500">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-orange-700 mb-6 flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
                Special Talents
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {character.special_talents}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fun Facts - Full Width */}
      {funFactsList.length > 0 && (
        <Card className="border-l-4 border-purple-500">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-purple-700 mb-6 flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-3" aria-hidden="true"></div>
              Fun Facts
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" role="list">
              {funFactsList.map((fact: string, index: number) => (
                <li key={index} className="flex items-start p-4 bg-purple-50 rounded-lg" role="listitem">
                  <span className="text-purple-500 mr-3 mt-1 text-xl" aria-hidden="true">•</span>
                  <span className="text-gray-700 leading-relaxed">{fact}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
